import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./StreakScreen.module.css";
import redesignStyles from "./StreakScreenRedesign.module.scss";
import AnimatedNumber from "./AnimatedNumber.jsx";
import WeekStreakRow from "./WeekStreakRow.jsx";
import StreakFire from "../../components/ui/Fire/StreakFire.jsx";
import { pickIgnition } from "../../components/ui/Fire/ignitionPicker.js";
import fireIgniteSfx from "../../Assets/sounds/fireIgnite.mp3";
import fireLoopSfx from "../../Assets/sounds/fireLoop.mp3";
import checkSfx from "../../Assets/sounds/check.mp3";
import useOverlayScrollLock from "../../hooks/useOverlayScrollLock.js";
/**
 * Tela de streak animada (mostrar DEPOIS do PublishButton fechar).
 *
 * Timeline:
 *   t=0       fogo apagado + fumaça; número mostra (streak - 1) em cinza
 *   t=2000ms  acende (ignição sorteada) + número rola p/ streak + vira laranja
 *   acendeu   aparece "day streak"
 *   +600ms    grupo sobe um pouco e revela a semana
 *   +1000ms   marca o dia de hoje (check desenha + arco sai)
 *   +1500ms   aparece o texto + botão Continuar
 *
 * Props:
 *   streak:          number    — novo valor do streak (ex.: 7, 10)
 *   weekActiveDays:  number[]  — dias já defendidos antes de hoje (0-6)
 *   todayIdx:        number    — índice de hoje (0-6); default = hoje
 *   onClose:         fn        — fecha a tela
 */
export default function StreakScreen({
  streak = 7,
  weekActiveDays = [],
  frozenDays = [],
  todayIdx = new Date().getDay(),
  onClose,
  redesign = false,
}) {
  // Overlay fixed fora do SwipeableOffcanvas → trava/restaura o scroller do app
  // enquanto a tela de streak estiver montada (Fase 0/item 5).
  useOverlayScrollLock(true);

  const [ignition] = useState(() => pickIgnition());
  const [ignite, setIgnite] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [showWeekMark, setShowWeekMark] = useState(false);
  const [showFooter, setShowFooter] = useState(false);

  const igniteAudio = useRef(null);
  const loopAudio   = useRef(null);
  const checkAudio  = useRef(null);

  // Cria os objetos de áudio uma vez e limpa ao desmontar
  useEffect(() => {
    igniteAudio.current = new Audio(fireIgniteSfx);
    loopAudio.current   = new Audio(fireLoopSfx);
    loopAudio.current.loop   = true;
    loopAudio.current.volume = 0.35;
    checkAudio.current  = new Audio(checkSfx);

    return () => {
      [igniteAudio, loopAudio, checkAudio].forEach(({ current: a }) => {
        if (!a) return;
        a.pause();
        a.src = "";
      });
    };
  }, []);

  // t=2s: acende + número rola + som de ignição
  useEffect(() => {
    const t = setTimeout(() => {
      setIgnite(true);
      igniteAudio.current?.play().catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  const handleIgnited = useCallback(() => {
    setShowLabel(true);
    // Fogo aceso → inicia loop
    loopAudio.current?.play().catch(() => {});

    const t1 = setTimeout(() => setRevealed(true), 600);
    const t2 = setTimeout(() => {
      setShowWeekMark(true);
      // +250ms sincroniza com o momento que o check aparece no WeekStreakRow
      setTimeout(() => checkAudio.current?.play().catch(() => {}), 250);
    }, 1000);
    const t3 = setTimeout(() => setShowFooter(true), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  return (
    <div className={`${styles.screen} ${redesign ? redesignStyles.screen : ""}`}>
      <div className={`${styles.centerGroup} ${revealed ? styles.centerUp : ""}`}>
        <StreakFire ignite={ignite} ignition={ignition} onIgnited={handleIgnited} />

        <div className={styles.numberWrap}>
          <AnimatedNumber from={Math.max(0, streak - 1)} to={streak} play={ignite} lit={ignite} />
        </div>

        <div className={`${styles.dayStreakLabel} ${showLabel ? styles.show : ""}`}>
          {redesign ? "dias de ofensiva" : "day streak"}
        </div>
      </div>

      <div className={`${styles.weekSection} ${revealed ? styles.show : ""}`}>
        <WeekStreakRow
          activeDays={weekActiveDays}
          frozenDays={frozenDays}
          todayIdx={todayIdx}
          newDayIdx={todayIdx}
          play={showWeekMark}
        />
        <p className={styles.weekHint}>registre todos os dias para manter a ofensiva</p>
      </div>

      <button
        type="button"
        className={`${styles.continueBtn} ${showFooter ? styles.show : ""} ${redesign ? redesignStyles.continue : ""}`}
        onClick={onClose}
      >
        Continuar
      </button>
    </div>
  );
}